import {defineStore} from 'pinia';
import api from "@/core/services/axios.js";

export const useUserNotificationStore = defineStore('userNotification', {
    state: () => ({
        rows: [],
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        unreadCount: 0,
        limit: 20,
        isLoading: false,
        error: null,
        _fetchController: null,
    }),
    actions: {
        async fetchData({page = 1, unreadOnly = false} = {}) {
            if (this._fetchController) {
                this._fetchController.abort();
            }
            const controller = new AbortController();
            this._fetchController = controller;

            try {
                this.isLoading = true;
                const response = await api.get('/notifications', {
                    params: {page, limit: this.limit, unread_only: unreadOnly || undefined},
                    signal: controller.signal,
                });
                if (controller.signal.aborted) return this.rows;

                const data = response?.data;
                if (Array.isArray(data?.data)) {
                    this.rows = data.data;
                    this.currentPage = data.current_page || 1;
                    this.totalPages = data.total_pages || 1;
                    this.totalCount = data.total_count || 0;
                }
                this.unreadCount = data?.unread_count ?? this.unreadCount;
                return this.rows;
            } catch (error) {
                if (error?.code === 'ERR_CANCELED') return this.rows;
                console.error('Fetch notifications error:', error);
                this.rows = [];
                throw error;
            } finally {
                if (!controller.signal.aborted) {
                    this.isLoading = false;
                    this._fetchController = null;
                }
            }
        },

        async fetchUnreadCount() {
            try {
                const response = await api.get('/notifications/unread-count');
                this.unreadCount = response?.data?.unread_count ?? 0;
                return this.unreadCount;
            } catch {
                return this.unreadCount;
            }
        },

        async markAsRead(notificationId) {
            try {
                await api.post(`/notifications/${notificationId}/read`);
                const n = this.rows.find(r => r.id === notificationId);
                if (n && !n.is_read) {
                    n.is_read = true;
                    if (this.unreadCount > 0) this.unreadCount--;
                }
            } catch (error) {
                console.error('Mark as read error:', error);
                throw error;
            }
        },

        async markAllAsRead() {
            try {
                await api.post('/notifications/read-all');
                this.rows.forEach(n => { n.is_read = true; });
                this.unreadCount = 0;
            } catch (error) {
                console.error('Mark all as read error:', error);
                throw error;
            }
        },

        async deleteNotification(notificationId) {
            try {
                await api.delete(`/notifications/${notificationId}`);
                const n = this.rows.find(r => r.id === notificationId);
                if (n && !n.is_read && this.unreadCount > 0) this.unreadCount--;
                this.rows = this.rows.filter(r => r.id !== notificationId);
            } catch (error) {
                console.error('Delete notification error:', error);
                throw error;
            }
        },

        async goToPage(page) {
            if (page < 1 || page > this.totalPages || page === this.currentPage) return;
            await this.fetchData({page});
        },

        clearState() {
            if (this._fetchController) {
                this._fetchController.abort();
                this._fetchController = null;
            }
            this.rows = [];
            this.currentPage = 1;
            this.totalPages = 1;
            this.totalCount = 0;
            this.isLoading = false;
            this.error = null;
        },
    },
});
